import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { authService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';

const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { key: 'uppercase', label: 'Uppercase (A\u2013Z)', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lowercase', label: 'Lowercase (a\u2013z)', test: (pw) => /[a-z]/.test(pw) },
  { key: 'number', label: 'Number (0\u20139)', test: (pw) => /[0-9]/.test(pw) },
];

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | no-session
  const [hasSession, setHasSession] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Check if user has a valid session from the reset link
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setHasSession(true);
      } else {
        setStatus('no-session');
      }
      setSessionChecked(true);
    };

    // Listen for auth state change (Supabase processes the recovery token from URL)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setHasSession(true);
        setSessionChecked(true);
      }
    });

    checkSession();

    return () => subscription.unsubscribe();
  }, []);

  const allRulesPassed = PASSWORD_RULES.every((rule) => rule.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRulesPassed && passwordsMatch && status === 'idle';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus('loading');
    setError('');

    try {
      const { error: updateError } = await authService.updatePassword(password);

      if (updateError) {
        setStatus('error');
        setError(updateError.message || 'Failed to reset password. Please try again.');
      } else {
        setStatus('success');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  };

  if (!sessionChecked) return null;

  // No valid session — user navigated here without a reset link
  if (status === 'no-session' && !hasSession) {
    return (
      <>
        <Helmet>
          <title>Reset Password | Roomwork.ai</title>
        </Helmet>
        <div className="h-screen flex items-center justify-center bg-black px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-red-400" />
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">Invalid or Expired Link</h1>
            <p className="text-gray-400 text-sm mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/forgot-password')}
              className="bg-[#2a2a2a] hover:bg-[#333333] text-white border border-[#2a2a2a] rounded-2xl px-6 py-3 font-medium transition-all shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)]"
            >
              Request New Link
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reset Password | Roomwork.ai</title>
        <meta name="description" content="Set a new password for your Roomwork.ai account." />
      </Helmet>

      <div className="h-screen overflow-hidden flex">
        {/* Left Side - Hero Image */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-black items-center justify-start">
          <img
            src="/assets/images/login-registration-hero.png"
            alt="Modern luxury interior design showcase"
            className="h-full object-contain object-left"
          />
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-black relative overflow-y-auto">
          <div className="absolute inset-0 bg-black"></div>
          <div className="relative z-10 w-full max-w-md px-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-2">Reset Password</h1>
              <p className="text-sm text-gray-400">Enter your new password below.</p>
            </div>

            {/* Success Message */}
            {status === 'success' && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-400 text-sm font-medium">Password reset successfully!</p>
                    <p className="text-green-400/70 text-xs mt-1">Redirecting to login...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="XCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (status === 'error') { setStatus('idle'); setError(''); }
                    }}
                    placeholder="Enter new password"
                    disabled={status === 'loading' || status === 'success'}
                    className="w-full px-4 py-3.5 pr-12 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
              </div>

              {/* Password Rules Checklist */}
              {password.length > 0 && (
                <div className="space-y-1.5">
                  {PASSWORD_RULES.map((rule) => {
                    const passed = rule.test(password);
                    return (
                      <div key={rule.key} className="flex items-center gap-2">
                        <Icon
                          name={passed ? 'Check' : 'Minus'}
                          size={14}
                          className={passed ? 'text-green-400' : 'text-gray-500'}
                        />
                        <span className={`text-xs ${passed ? 'text-green-400' : 'text-gray-500'}`}>
                          {rule.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (status === 'error') { setStatus('idle'); setError(''); }
                    }}
                    placeholder="Confirm new password"
                    disabled={status === 'loading' || status === 'success'}
                    className="w-full px-4 py-3.5 pr-12 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-red-400 text-xs mt-1.5">Passwords do not match</p>
                )}
                {passwordsMatch && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <Icon name="Check" size={14} className="text-green-400" />
                    <span className="text-green-400 text-xs">Passwords match</span>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={status === 'loading'}
                disabled={!canSubmit || status === 'loading' || status === 'success'}
                className="bg-[#2a2a2a] hover:bg-[#333333] text-white border border-[#2a2a2a] rounded-2xl py-3.5 font-medium transition-all shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] focus:outline-none focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] active:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)]"
              >
                Reset Password
              </Button>
            </form>

            {/* Back */}
            <div className="mt-6">
              <Button
                variant="ghost"
                fullWidth
                onClick={() => navigate('/login')}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}
              >
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
